import Text from './text';
import { eq } from 'tio-ui/utilities';
import { get } from '@ember/helper';
import type { TOC } from '@ember/component/template-only';
import { getPipelineTokens } from 'tio-reimagined/color-tokens';

// ---------------------------------------------------------------------------
// PipelineStage – a single stage in a pipeline flow visualization
// ---------------------------------------------------------------------------

export type PipelineStageColor = 'violet' | 'teal' | 'green' | 'gray' | 'amber';
export type PipelineStageSize = 'sm' | 'md' | 'lg';

interface PipelineStageSignature {
  Args: {
    /** The numeric or string value to display */
    value: string | number;
    /** Label text below the value */
    label: string;
    /** Color theme for the stage */
    color?: PipelineStageColor;
    /** Size variant - sm for compact, md for default, lg for bordered */
    size?: PipelineStageSize;
  };
  Element: HTMLDivElement;
}

const pipelineColorClasses = {
  violet: getPipelineTokens('violet'),
  teal: getPipelineTokens('teal'),
  green: getPipelineTokens('green'),
  gray: getPipelineTokens('gray'),
  amber: getPipelineTokens('amber'),
};

const sizeClasses: Record<PipelineStageSize, string> = {
  sm: 'p-3',
  md: 'p-4',
  lg: 'px-8 py-4 border-2',
};

/**
 * PipelineStage - A single stage in a pipeline flow visualization
 *
 * Displays a value and label in a colored box, typically used within a
 * horizontal flow with arrows between stages.
 *
 * @example
 * ```gts
 * import PipelineStage from 'tio-reimagined/components/pipeline-stage';
 *
 * <PipelineStage @value={{85}} @label="In Pipeline" @color="violet" />
 * ```
 */
const PipelineStage: TOC<PipelineStageSignature> = <template>
  {{#let (if @color @color "violet") as |color|}}
    {{#let (if @size @size "md") as |size|}}
      {{#let (get pipelineColorClasses color) as |colors|}}
        <div
          data-test-pipeline-stage
          class="text-center rounded-lg
            {{get sizeClasses size}}
            {{colors.bg}}
            {{if (eq size 'lg') colors.border}}"
          ...attributes
        >
          <div data-test-pipeline-stage-value class="text-3xl font-bold {{colors.text}}">
            {{@value}}
          </div>
          <Text data-test-pipeline-stage-label @variant="muted" @size="sm">{{@label}}</Text>
        </div>
      {{/let}}
    {{/let}}
  {{/let}}
</template>;

export default PipelineStage;

export type { PipelineStageSignature };
